import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce';
import { initialSideBarState, SideBarContext } from 'context/sidebarContextWrapper';
import { FUSION_SERVER_TAG, OTHER_PRODUCTS_TAG } from 'config/productItems';
import { Link, navigate } from 'gatsby';
import { defaultSearchParams, paramNames } from 'config/search';
import Fuse from 'fuse.js';
import {filterContainer, filterInputWrap, filterIconWrap, filterSidenav, dropdownWrap, search, arrow} from './sidenavfilter.styles.module.scss'

const fuseOptionsProduct = {
    keys: ['name'],
    threshold: 0.6,
    minMatchCharLength: 2,
  };

const fuseOptionsHowTo = {
   keys: ['node.frontmatter.title'],
   threshold: 0.6,
   minMatchCharLength: 2,
}


const SidenavFilter = React.memo(function SidenavFilterMemo({ 
  flattenedItems = [], 
  howToRelated = [], 
  version 
}){

  const state = useContext(SideBarContext) || initialSideBarState;
  const [toggleOpen, setToggleOpen] = useState(false);
  const [fuzzyResults, setFuzzyResults] = useState([])
  const [focusFilterIndex, setFocusFilterIndex] = useState(-1);
  const [prevFocusFilterIndex, setPrevFocusFilterIndex] = useState(-1)
  
  const inputFilterRef = useRef();
  const dropdownRef = useRef();

  const [inputLength, setInputLength] = useState(inputFilterRef.current?.value.length)


    // Close dropdown if user clicks outside of results list
    const handleClickOutside = event => {
      const { current: dropdown } = dropdownRef;
      if (
        dropdown &&
        !dropdown.contains(event.target) &&
        !dropdownRef.current.contains(event.target) &&
        !inputFilterRef.current.contains(event.target)
      ) {
        setToggleOpen(false);
      } 
    };

    useEffect(() => {
      if((focusFilterIndex >= fuzzyResults.length + 1) || (focusFilterIndex >= 6)){
        setFocusFilterIndex(-1)
      }
      setPrevFocusFilterIndex(focusFilterIndex)
    }, [focusFilterIndex, fuzzyResults.length])

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if(inputFilterRef.current?.value.length !== inputLength){
      setInputLength(inputFilterRef.current.value.length)
      setFocusFilterIndex(-1)
    }

    const handleFilterChange = (value, compare, howTo) => {
      let fuse;
      if(compare.length > 0){
        fuse = new Fuse(compare, fuseOptionsProduct)
      } else (
        fuse = new Fuse(howTo, fuseOptionsHowTo)
      )
      const result = fuse.search(`${value}`);
      if(value.length > 0) {
        setToggleOpen(true)
      } else setToggleOpen(false)
      setFuzzyResults(result)

      if(value.length === 0){
        setToggleOpen(false)
      }
    }

    // Debounce user input with time specified in ms
    const debounceOnChange = useCallback(debounce(handleFilterChange, 200), []); // eslint-disable-line react-hooks/exhaustive-deps

    const routeFilterQuery = () => {
      const query = inputFilterRef.current.value;
      const params = { ...defaultSearchParams.params, wt: 'json', q: query };
      let url = '/search?' + new URLSearchParams(params).toString();  /* eslint-disable-line prefer-template */
      
      if(state.currentProduct.tag === FUSION_SERVER_TAG){
        url += `&fq=productName:("fusion-server","fusion-ai","app-studio")&fq=${paramNames.version}:("${version}")`;
      }
      else {
        url += `&fq=productName:("${state.currentProduct.tag}")&fq=${paramNames.version}:("${version}")`;
      }

      navigate(url)
    }

    const handleKeyDown = event => {
      if(event.keyCode === 38){
        event.preventDefault()
        setFocusFilterIndex(prev => 
          prev === -1 ? 5 : prev - 1
        )
      }
      if(event.keyCode === 40){
       setFocusFilterIndex(prev => prev + 1)
      }
      if(event.keyCode === 13 && focusFilterIndex !== -1){ // focusFilterIndex !== -1 means enter is on input box value/focus
       setFocusFilterIndex(44)
        return
      }
      if (event.key === 'Enter' && focusFilterIndex !== 44) {
        routeFilterQuery();
      }
    };



    if(focusFilterIndex === 44){
      if(prevFocusFilterIndex !== 5){
        navigate(`/${fuzzyResults[prevFocusFilterIndex].item.link}`)
      } else {
        routeFilterQuery()
      }
    }

    return (
        <div className={filterContainer}>
            <label htmlFor="filterSidenav" className={filterInputWrap}>
                <span className={filterIconWrap}>
                  <img className="filter-search-icon" alt="filter search icon" src="data:," width="24px" height="25px"/>
                </span>
                <input 
                    ref={inputFilterRef}
                    style={{zIndex: '-1'}}
                    type="text" 
                    id="filterSidenav" 
                    name="filterSidenav"
                    className={filterSidenav}  
                    placeholder={howToRelated.length > 0 ? 'Filter how-to titles' : 'Filter titles'}
                    aria-label="filter-sidenav" 
                    onChange={e => debounceOnChange(e.target.value, flattenedItems, howToRelated)}
                    onKeyDown={handleKeyDown}
                    maxLength={50}
                />
            </label>


            {toggleOpen && 
              <div className={dropdownWrap} aria-expanded={toggleOpen} ref={dropdownRef}>
                <ul>
                  {fuzzyResults.slice(0, 5).map((result, indx) => {
                    if(howToRelated.length > 0){
                      return <li key={result.item.node.frontmatter.permalink} style={indx === focusFilterIndex ? {background: 'var(--color-hover'} : {}}><Link to={`/how-to/${result.item.node.frontmatter.permalink}/${result.item.node.frontmatter.title}`} >{result.item.node.frontmatter.title}</Link></li>
                    } 
                    return <li key={result.item.link} style={indx === focusFilterIndex ? {background: 'var(--color-hover'} : {}}><Link to={`/${result.item.link}`} >{result.item.name}</Link></li>
                  })}
                  {
                    state.currentProduct.tag !== OTHER_PRODUCTS_TAG &&
                    <li className={search} style={focusFilterIndex === 5 ? {background: 'var(--color-hover'} : {}}>
                      <button role="link" type="button" aria-label="search filter keyword" tabIndex={0} onClick={routeFilterQuery}>
                        <span className={arrow}>→</span> Search: <span style={{fontStyle:'italic', marginLeft: '7px'}}>
                          {inputFilterRef.current.value}
                        </span>
                      </button>
                    </li>
                  }
                </ul>
            </div>
            }

        </div>
    )
})


SidenavFilter.propTypes = {
 flattenedItems: PropTypes.array,
 howToRelated: PropTypes.array,
 version: PropTypes.string.isRequired,
};


export default SidenavFilter